import { type FC, memo, useEffect, useMemo, useRef } from "react";
import { token } from "@stubhub/broadway-foundation/tokens";
import { Button, ButtonKind, ButtonSize, ButtonVariant, Text } from "@stubhub/broadway-web";
import { useContent } from "src/shared/components/content";
import { useAppContext } from "src/shared/context/AppContext";
import {
  calendarContainer,
  dateButtonStyles,
  datesContainer,
  datesContainerWrapper,
  eventIndicatorDot,
  weekdayItem,
  weekdaysHeader,
} from "src/website-apps/responsive/stubhub-performer/components/calendar/mobile/styles";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { useStableNow } from "src/website-apps/shared/hooks/useStableNow";
import { getMonthDateKey } from "src/website-apps/shared/utils/calendar";
import { formatDateString, getCalendarDays, parseDateString } from "../utils";

interface Props {
  activeDateString?: string | undefined;
  onDateClick?: (dayOfMonth: number, dateString: string) => void;
}

interface CalendarDay {
  // Original month day (null for padding)
  day: number | null;
  // Month key used to look up events for this cell (derived from actual date)
  monthKey: string;
  // Date string identifier (YYYY-MM-DD)
  dateString: string;
  // Day-of-month to display in the button (derived from actual date)
  displayDay: number;
  // Day of week for snap alignment (0 = Sunday)
  dayOfWeek: number;
  // The originating month context
  monthDate: Date;
}

const isHTMLElement = (element: Element | null): element is HTMLElement =>
  element !== null && element instanceof HTMLElement;

export const HorizontalCalendar: FC<Props> = memo(({ activeDateString, onDateClick }) => {
  const [state] = useEventsGridContext();
  const datesContainerRef = useRef<HTMLDivElement>(null);
  const pageModelData = usePerformerPageModelData();
  const appContext = useAppContext();
  const today = useStableNow();

  const { calendarMonthsWithEventsAsDates, calendarEventsByMonthAndDay, filters } = state;

  const teamPrimaryColor = pageModelData.categoryMetadata?.sports?.partnerTheme?.primaryColor;

  const sunContent = useContent("cid$#3562197#"); // "Sun"
  const monContent = useContent("cid$#3562198#"); // "Mon"
  const tueContent = useContent("cid$#3562199#"); // "Tue"
  const wedContent = useContent("cid$#3562200#"); // "Wed"
  const thuContent = useContent("cid$#3562201#"); // "Thu"
  const friContent = useContent("cid$#3562202#"); // "Fri"
  const satContent = useContent("cid$#3562203#"); // "Sat"

  // Sunday-start week order
  const daysOfWeek = useMemo(
    () => [sunContent, monContent, tueContent, wedContent, thuContent, friContent, satContent],
    [sunContent, monContent, tueContent, wedContent, thuContent, friContent, satContent],
  );

  const calendarDays = useMemo(() => {
    const allDays: CalendarDay[] = [];

    for (const [idx, monthDate] of calendarMonthsWithEventsAsDates.entries()) {
      const monthDays = getCalendarDays(monthDate, filters.dates, today);

      if (monthDays.length === 0) continue;

      // Identify padding ranges
      const firstRealIdx = monthDays.findIndex((d) => d.day !== null);
      const lastRealIdx = (() => {
        for (let i = monthDays.length - 1; i >= 0; i -= 1) {
          if (monthDays[i]?.day !== null) return i;
        }
        return -1;
      })();

      const isFirstMonth = idx === 0;
      const isLastMonth = idx === calendarMonthsWithEventsAsDates.length - 1;
      // For continuity across months: include left padding only for the first month,
      // and include right padding only for the last month.
      const startIndex = isFirstMonth ? 0 : firstRealIdx;
      const endIndex = isLastMonth ? monthDays.length : lastRealIdx + 1;
      const sliced = monthDays.slice(startIndex, endIndex);
      const localFirstRealIdx = isFirstMonth ? firstRealIdx : 0;

      // Precompute first real date for left padding mapping
      const firstRealDay = monthDays[firstRealIdx]?.day ?? 1;
      const firstRealDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), firstRealDay);

      for (const entry of sliced.entries()) {
        const localIndex = entry[0];
        const { day } = entry[1];
        let actualDate: Date;
        if (day) {
          actualDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
        } else if (localIndex < localFirstRealIdx) {
          // Left padding: compute the actual date based on offset from first real date
          const offset = localFirstRealIdx - localIndex;
          actualDate = new Date(firstRealDate);
          actualDate.setDate(firstRealDate.getDate() - offset);
        } else {
          // Remaining nulls (should only occur for last month's end padding)
          const lastRealDay = monthDays[lastRealIdx]?.day ?? 1;
          const lastRealDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), lastRealDay);
          const offset = localIndex - lastRealIdx;
          actualDate = new Date(lastRealDate);
          actualDate.setDate(lastRealDate.getDate() + offset);
        }

        const dateString = formatDateString(actualDate);
        const eventsMonthKey = getMonthDateKey(actualDate);
        const displayDay = actualDate.getDate();

        allDays.push({
          day,
          monthKey: eventsMonthKey,
          dateString,
          displayDay,
          dayOfWeek: actualDate.getDay(),
          monthDate,
        });
      }
    }

    return allDays;
  }, [calendarMonthsWithEventsAsDates, filters.dates, today]);

  // Prevent user scrolling on the dates container
  useEffect(() => {
    const container = datesContainerRef.current;
    if (!container) return undefined;

    const preventScroll = (e: Event) => {
      e.preventDefault();
    };

    container.addEventListener("wheel", preventScroll, { passive: false });
    container.addEventListener("touchmove", preventScroll, { passive: false });

    return () => {
      container.removeEventListener("wheel", preventScroll);
      container.removeEventListener("touchmove", preventScroll);
    };
  }, []);

  // Auto-scroll to active date when it changes
  useEffect(() => {
    if (!(activeDateString && datesContainerRef.current)) {
      return;
    }

    const activeElement = datesContainerRef.current.querySelector(`[data-date-cell="${CSS.escape(activeDateString)}"]`);

    if (activeElement) {
      const container = datesContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const elementRect = activeElement.getBoundingClientRect();

      // Add padding to ensure element is fully visible with some clearance
      const minClearance = 8;
      const isElementVisible =
        elementRect.left >= containerRect.left + minClearance &&
        elementRect.right <= containerRect.right - minClearance;

      if (!isElementVisible) {
        // Parse the active date to get the target date in local time
        const targetDate = parseDateString(activeDateString);
        const dayOfWeek = targetDate.getDay(); // 0 = Sunday, 1 = Monday, etc.

        const sundayDate = new Date(targetDate);
        sundayDate.setDate(targetDate.getDate() - dayOfWeek);

        const sundayDateString = formatDateString(sundayDate);

        const sundayElement = container.querySelector(`[data-date-cell="${CSS.escape(sundayDateString)}"]`);
        if (isHTMLElement(sundayElement)) {
          const sundayElementLeft = sundayElement.offsetLeft;
          const leftPadding = 16;
          const scrollLeft = sundayElementLeft - leftPadding;

          container.scrollTo({
            left: Math.max(0, scrollLeft),
            behavior: "smooth",
          });
        }
      }
    }
  }, [activeDateString]);

  const handleDateClick = (dayOfMonth: number | null, dateString: string, monthKey: string) => {
    const dayEvents = calendarEventsByMonthAndDay[monthKey]?.[dayOfMonth ?? 0] ?? [];
    if (dayOfMonth && dayEvents.length > 0 && onDateClick) {
      onDateClick(dayOfMonth, dateString);
    }
  };

  return (
    <div className={calendarContainer}>
      <div className={weekdaysHeader}>
        {daysOfWeek.map((dayName) => (
          <div key={dayName} className={weekdayItem}>
            <Text variant="label" size="extra-small">
              {dayName}
            </Text>
          </div>
        ))}
      </div>

      <div className={datesContainerWrapper}>
        <div ref={datesContainerRef} className={datesContainer}>
          {calendarDays.map(({ displayDay, monthKey, dateString, dayOfWeek }) => {
            const dayEvents = calendarEventsByMonthAndDay[monthKey]?.[displayDay] ?? [];
            const hasEvents = dayEvents.length > 0;
            const isActive = dateString === activeDateString;
            const isDisabled = !hasEvents;

            const hasHomeGame = dayEvents.some(
              (event) => appContext.categoryId === event.eventMetadata?.sports?.homeTeamId,
            );

            const buttonClasses = dateButtonStyles({
              activation: isActive ? "active" : "inactive",
              interactivity: isDisabled ? "disabled" : "enabled",
            });

            // Add scroll snap align to actual Sundays
            const isSunday = dayOfWeek === 0;
            const snapStyle = isSunday ? { scrollSnapAlign: "start" } : {};

            return (
              <Button
                key={dateString}
                data-date-cell={dateString}
                variant={ButtonVariant.Regular}
                kind={ButtonKind.Neutral}
                size={ButtonSize.Small}
                classes={buttonClasses}
                style={snapStyle}
                onClick={
                  isDisabled
                    ? undefined
                    : () => {
                        handleDateClick(displayDay, dateString, monthKey);
                      }
                }
                disabled={isDisabled}
                aria-label={hasEvents ? `View events for ${displayDay}` : `No events on ${displayDay}`}
              >
                <Text variant="label" size="small">
                  {displayDay}
                </Text>
                {hasEvents && (
                  <div
                    className={eventIndicatorDot}
                    style={{
                      backgroundColor:
                        hasHomeGame && teamPrimaryColor ? teamPrimaryColor : token("colors.border.strong"),
                    }}
                  />
                )}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
});

HorizontalCalendar.displayName = "HorizontalCalendar";
